import { registerBiometric } from '@api/apiCall';
import { Text, View } from '@components/primitives';
import { useToast } from '@hooks';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import { getBioTokenAsync, saveBiometricBioToken } from '@utils/biometricUtils';
import { showSuccessToast } from '@utils/toastManager';
import { MaterialIcons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity } from 'react-native';
import styles from './styles';

const TOAST_ID_BIOMETRIC_ACTIVATED = 'biometric-activated-banner';

function BiometricBanner(): React.JSX.Element {
  const { t } = useTranslation('common');
  const toast = useToast();
  const { setBiometricEnrolled } = getStore();
  const [loading, setLoading] = useState(false);

  const handlePress = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const LocalAuthentication = await import('expo-local-authentication');
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      if (!hasHardware || !isEnrolled) {
        setLoading(false);
        return;
      }
      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: t('biometricEncouragement.cta'),
        disableDeviceFallback: true,
      });
      if (result.success !== true) {
        setLoading(false);
        return;
      }
      const bioToken = await getBioTokenAsync();
      await registerBiometric(bioToken);
      await saveBiometricBioToken(bioToken);
      setBiometricEnrolled(true);
      showSuccessToast(toast, t('biometricToast.activated'), TOAST_ID_BIOMETRIC_ACTIVATED);
    } catch (_err) {
      // Stay on Home; user can tap again
    } finally {
      setLoading(false);
    }
  };

  if (Platform.OS !== 'ios' && Platform.OS !== 'android') return null;

  return (
    <TouchableOpacity testID="btnBiometricBanner" onPress={handlePress} disabled={loading}>
      <View style={styles.bannerContainer}>
        <MaterialIcons name="lock-open" size={24} color={colors.warning.sunflower} />
        <View style={styles.textContainer}>
          <Text variant="sm-bold" color={colors.neutral.charcoal}>
            {t('biometricBanner.message')}
          </Text>
          <Text variant="sm-bold" color={colors.secondary.orchid} underline>
            {t('biometricBanner.cta')}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
}

export default BiometricBanner;
